import React, { useState } from "react";
import axios from 'axios'; // Import axios
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheck, FaSpinner, FaTimes } from "react-icons/fa"; // Add spinner and times icons
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { trackConversion } from '../components/AnalyticsTracker';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';
import ProcessSteps from '../components/page/ProcessSteps';
import ProofStrip from '../components/page/ProofStrip';
import contactHero from '../assets/contact/contact-hero.webp';
import placeImage from '../assets/contact/place-zadar-coast.webp';

const Contact = () => {
  const { t } = useTranslation();

  // Colors for consistent theming
  const primaryTextColor = "text-silver-light"; // Very light gray/silver
  const secondaryTextColor = "text-silver-medium"; // Medium gray/silver

  // One field style for the whole form. Focus ring is moss, not silver: the
  // focused field and the submit button are the two things the eye should
  // find, and they should agree.
  const fieldBase =
    `bg-black border rounded-md w-full py-2.5 px-3 ${primaryTextColor} leading-tight ` +
    "placeholder:text-silver-dark focus:outline-none focus:ring-1 focus:ring-moss focus:border-moss transition";
  const fieldClass = (hasError) => `${fieldBase} ${hasError ? "border-red-500" : "border-gray-700"}`;

  const initialFormData = {
    name: "",
    email: "",
    service: "Select a service",
    message: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'sending', 'success', 'error'
  const [apiError, setApiError] = useState(''); // Store API error messages
  const [formSubmitted, setFormSubmitted] = useState(false); // Keep this to show overall success message
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    if (errors[e.target.id]) setErrors({ ...errors, [e.target.id]: null });
  };

  const validate = () => {
    let tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = t('contact.validation.nameRequired');
    if (!formData.email) {
      tempErrors.email = t('contact.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = t('contact.validation.emailInvalid');
    }
    if (formData.service === t('contact.form.servicePlaceholder')) tempErrors.service = t('contact.validation.serviceRequired');
    if (!formData.message.trim()) tempErrors.message = t('contact.validation.messageRequired');
    return tempErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setSubmitStatus('sending');
      setIsModalOpen(true);
      setApiError(''); // Clear previous errors

      try {
        // Use /api/messages endpoint
        const response = await axios.post('/api/messages', formData);

        if (response.data.success) {
          setSubmitStatus('success');
          setFormSubmitted(true); // Show the green success banner above form
          setFormData(initialFormData); // Reset form fields
          // The goal that actually matters. Recorded only on a confirmed
          // success so a failed submit is never counted as a lead.
          trackConversion('contact_form');
          // Optional: Auto-close modal after a delay
          // setTimeout(() => setIsModalOpen(false), 2000);
        } else {
          // Handle backend validation errors or other non-2xx success responses
          setSubmitStatus('error');
          setApiError(response.data.message || t('contact.modal.errorMessageDefault'));
        }
      } catch (error) {
        setSubmitStatus('error');
        let message = t('contact.modal.errorMessageDefault');
        if (error.response) {
          // Server responded with a status code outside 2xx range
          console.error('Server Error:', error.response.data);
          message = error.response.data.message || message; // Use server message if available
          if (error.response.status === 429) { // Specific message for rate limit
            message = t('contact.modal.errorRateLimit');
          }
        } else if (error.request) {
          // Request was made but no response received
          console.error('Network Error:', error.request);
          message = t('contact.modal.errorNetwork');
        } else {
          // Something else happened setting up the request
          console.error('Error:', error.message);
        }
        setApiError(message);
      }
    }
  };

  const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [nextRef, nextInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  // Deliberately the SAME three steps the /services page publishes, worded for
  // someone who is already at the form. Saying what happens next — and when a
  // price appears — is what removes the "what am I agreeing to" hesitation
  // that sits between reading the form and sending it.
  const processSteps = [
    {
      title: t('contact.process.step1.title', 'You send this form'),
      text: t('contact.process.step1.text', 'Four fields. No account to create, nothing to install.'),
    },
    {
      title: t('contact.process.step2.title', 'We talk it through'),
      text: t('contact.process.step2.text', 'A free 30-minute call to understand what you need and whether we are the right fit.'),
    },
    {
      title: t('contact.process.step3.title', 'You get a written scope and price'),
      text: t('contact.process.step3.text', 'Agreed in writing before any work begins, so the cost never moves under you.'),
    },
  ];

  const services = [
    t('contact.services.webDev'),
    t('contact.services.ecommerce'),
    t('contact.services.business'),
    t('contact.services.portfolio'),
    t('contact.services.booking'),
    t('contact.services.consulting'),
    t('contact.services.other')
  ];

  // Modal Content Logic
  const renderModalContent = () => {
    switch (submitStatus) {
      case 'sending':
        return (
          <>
            <FaSpinner className="animate-spin text-silver-light text-3xl mb-4 mx-auto" />
            <h3 className={`text-xl font-medium mb-4 ${primaryTextColor}`}>{t('contact.modal.sendingTitle')}</h3>
            <p className={`${secondaryTextColor} text-sm`}>{t('contact.modal.sendingMessage')}</p>
          </>
        );
      case 'success':
        return (
          <>
            <FaCheck className="text-moss-light text-4xl mb-4 mx-auto" />
            <h3 className={`text-xl font-medium mb-4 ${primaryTextColor}`}>{t('contact.modal.successTitle')}</h3>
            <p className={`${secondaryTextColor} text-sm`}>{t('contact.modal.successMessage')}</p>
          </>
        );
      case 'error':
        return (
          <>
            <FaTimes className="text-red-500 text-4xl mb-4 mx-auto" />
            <h3 className={`text-xl font-medium mb-4 ${primaryTextColor}`}>{t('contact.modal.errorTitle')}</h3>
            <p className={`${secondaryTextColor} text-sm`}>{apiError || t('contact.modal.errorMessageDefault')}</p>
          </>
        );
      default:
        return null; // Should not happen if modal is open only during submission states
    }
  };

  return (
    <div className="bg-black min-h-screen font-sans relative">
      <SEO
        title="Contact Riviera Tech | Hire a Web Developer for Your Business Website"
        description="Contact Riviera Tech Solutions for custom web development services: custom websites, web application development, and ecommerce website development. Tell us your goals and get a practical build plan."
        keywords="hire web developer Croatia, contact custom website developer, custom web development quote, website developer for business, web development services Croatia, web developer Zadar"
        url="/contact"
      />
      <PageHero
        eyebrow={t('contact.hero.eyebrow', 'Contact')}
        title={t('contact.hero.title')}
        subtitle={t('contact.hero.subtitle')}
        ctaLabel={t('contact.hero.cta', 'Tell us about your project')}
        ctaHref="#project-form"
        proofLine={t('contact.hero.proof', 'Rated 5.0 on Google · Free 30-minute consultation · Fixed price agreed before work starts')}
        backgroundImage={contactHero}
      />

      {/* The form sits directly under the hero on purpose. A visitor who has
          reached /contact has already decided to make contact — the job here is
          to remove friction, not to keep persuading. Proof and reassurance go
          BELOW the form, for the ones still weighing it up. */}
      <section
        id="project-form"
        ref={contentRef}
        className={`scroll-mt-24 py-16 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
      >
        <div className="container mx-auto px-4">
          <div className="bg-off-black rounded-lg overflow-hidden max-w-5xl mx-auto border border-gray-800">
            <div className="flex flex-col md:flex-row">
              {/* Form Column */}
              <div className="md:w-7/12 p-6 md:p-8">
                {formSubmitted && submitStatus !== 'error' && (
                  <div className="bg-moss-deep/25 border border-moss/40 text-moss-light px-4 py-4 rounded-lg flex items-center mb-6">
                    <FaCheck className="text-moss-light mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-sm">{t('contact.form.successTitle')}</h3>
                      <p className="text-sm">{t('contact.form.successMessage')}</p>
                    </div>
                  </div>
                )}

                <h2 className={`text-2xl font-serif font-medium mb-6 ${primaryTextColor}`}>{t('contact.form.title')}</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${secondaryTextColor}`}
                      htmlFor="name"
                    >
                      {t('contact.form.name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={fieldClass(errors.name)}
                      id="name"
                      type="text"
                      placeholder={t('contact.form.namePlaceholder')}
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name && (
                      <p className="text-red-400 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${secondaryTextColor}`}
                      htmlFor="email"
                    >
                      {t('contact.form.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={fieldClass(errors.email)}
                      id="email"
                      type="email"
                      placeholder={t('contact.form.emailPlaceholder')}
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${secondaryTextColor}`}
                      htmlFor="service"
                    >
                      {t('contact.form.service')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={fieldClass(errors.service)}
                      id="service"
                      value={formData.service}
                      onChange={handleChange}
                    >
                      <option disabled>{t('contact.form.servicePlaceholder')}</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                    {errors.service && (
                      <p className="text-red-400 text-xs mt-1">{errors.service}</p>
                    )}
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${secondaryTextColor}`}
                      htmlFor="message"
                    >
                      {t('contact.form.message')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className={fieldClass(errors.message)}
                      id="message"
                      placeholder={t('contact.form.messagePlaceholder')}
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                    ></textarea>
                    {errors.message && (
                      <p className="text-red-400 text-xs mt-1">{errors.message}</p>
                    )}
                  </div>

                  {/* The one primary action on the page, so it carries the one
                      accent colour. Everything else here is a neutral. */}
                  <div className="pt-2">
                    <button
                      className={`inline-flex items-center rounded-md bg-moss px-7 py-3 font-medium text-black transition-colors duration-300 hover:bg-moss-light focus:outline-none focus:ring-2 focus:ring-moss-light focus:ring-offset-2 focus:ring-offset-off-black ${submitStatus === 'sending' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      type="submit"
                      disabled={submitStatus === 'sending'}
                    >
                      {submitStatus === 'sending' ? (
                        <FaSpinner className="animate-spin mr-2" />
                      ) : null}
                      {submitStatus === 'sending' ? t('contact.form.submitting') : t('contact.form.submit')}
                    </button>
                    <p className={`mt-3 text-xs ${secondaryTextColor}`}>
                      {t('contact.form.reassurance', 'No account needed. We use your details only to reply about this enquiry.')}
                    </p>
                  </div>
                </form>
              </div>

              {/* Contact Info Column */}
              <div className="md:w-5/12 bg-black border-t md:border-t-0 md:border-l border-gray-800 p-6 md:p-8">
                <h2 className={`text-2xl font-serif font-medium mb-6 ${primaryTextColor}`}>{t('contact.info.title')}</h2>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-moss-deep/40 p-2 rounded-full mr-4">
                      <FaPhone className="text-moss-light text-sm" />
                    </div>
                    <div>
                      <h3 className={`font-medium text-sm ${primaryTextColor}`}>{t('contact.info.phone')}</h3>
                      <p className={`text-sm ${secondaryTextColor}`}>+385 0992 05 44 51</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-moss-deep/40 p-2 rounded-full mr-4">
                      <FaEnvelope className="text-moss-light text-sm" />
                    </div>
                    <div>
                      <h3 className={`font-medium text-sm ${primaryTextColor}`}>{t('contact.info.email')}</h3>
                      <p className={`text-sm ${secondaryTextColor}`}>kris@rivieratech.solutions</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-moss-deep/40 p-2 rounded-full mr-4">
                      <FaMapMarkerAlt className="text-moss-light text-sm" />
                    </div>
                    <div>
                      <h3 className={`font-medium text-sm ${primaryTextColor}`}>{t('contact.info.office')}</h3>
                      <p className={`text-sm ${secondaryTextColor}`}>
                        {t('contact.info.addressLine1')}<br />
                        {t('contact.info.addressLine2')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <iframe
                    className="w-full h-40 rounded-lg border border-gray-800"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2861.5944936582614!2d15.35639487655217!3d44.1742168185771!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4761ee71f4427001%3A0x633d5df0774b72f6!2sGrabi%2026%2C%2023241%2C%20Poli%C4%8Dnik!5e0!3m2!1sen!2shr!4v1751636712074!5m2!1sen!2shr"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={t('contact.info.mapTitle')}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What happens after you press send */}
      <section ref={nextRef} className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4">
          <SectionHeading
            eyebrow={t('contact.process.eyebrow', 'What happens next')}
            title={t('contact.process.title', 'From this form to an agreed price')}
            className="mb-12"
          />
          <div
            className={`mx-auto max-w-5xl transition-all duration-1000 transform ${nextInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <ProcessSteps steps={processSteps} />
          </div>
        </div>
      </section>

      {/* Proof sits after the form, not before it — see the note on the form. */}
      <ProofStrip />

      {/* Closing band: where we actually are. The address is already in the
          info column; this gives it a face. */}
      <section className="relative overflow-hidden">
        <img
          src={placeImage}
          alt=""
          aria-hidden="true"
          className="h-72 w-full object-cover md:h-96"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-12">
            <p className="text-moss-light text-sm font-medium tracking-[0.2em] uppercase mb-3">
              {t('contact.place.eyebrow', 'Where we are')}
            </p>
            <p className="max-w-2xl text-xl md:text-2xl font-serif text-silver-light leading-snug">
              {t('contact.place.line', 'Based in Poličnik, Zadar County — working with businesses across Croatia and beyond.')}
            </p>
          </div>
        </div>
      </section>

      {/* Submission Status Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-off-black rounded-lg p-6 md:p-8 border border-gray-700 max-w-sm w-full text-center shadow-xl">
            {renderModalContent()}
            <button
              onClick={() => setIsModalOpen(false)} // Always allow closing
              className="mt-6 bg-silver-dark hover:bg-silver-medium text-black font-medium py-2 px-5 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-off-black focus:ring-silver-light transition duration-300"
            >
              {t('contact.modal.closeButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;